import React from 'react'
import Joi from 'joi'
import { t } from 'i18next'
import { FileCopy as CopyIcon } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useDispatch } from '../../store'
import { createEnterpriseUser } from '../../store/user/actionCreator'
import { CreateEnterpriseUserResult } from '../../store/user/actionType'
import {
  ErrorsState,
  notRequired,
  preventCyrillicOnInput,
  regex,
  validateForm,
} from '../../function/validation'
import { FormError } from '../../component/FormError'

// All fields on this form are optional — espy-backend's CreateEnterpriseUserDto
// only validates values that are actually provided (an admin can issue an
// access key first and fill in the person's details later). The shared
// `latinOnly` schema in function/validation.ts is `.required()`, so build a
// local allow-empty variant here instead of reusing it directly.
const optionalLatinOnly = Joi.string()
  .regex(regex.latinOnly)
  .allow('')
  .messages({ 'string.pattern.base': t('message.error.latinOnly') })

const schema = Joi.object({
  firstname: optionalLatinOnly.label(t('field.label.firstname')),
  lastname: optionalLatinOnly.label(t('field.label.lastname')),
  companyName: notRequired.label(t('field.label.companyName')),
})

// Small "(Optional)" suffix next to a FormLabel — every field on this form is
// optional (see schema above), so every row gets one.
function OptionalLabel({ label }: { label: React.ReactNode }) {
  return (
    <FormLabel>
      {label}{' '}
      <Typography component="span" variant="caption" color="textSecondary">
        ({t('common.optional')})
      </Typography>
    </FormLabel>
  )
}

type Props = {
  onClose: () => void
  onCreated: () => void
}

export function PopupCreateEnterpriseUser({ onClose, onCreated }: Props) {
  const dispatch = useDispatch()

  const [firstname, setFirstname] = React.useState('')
  const [lastname, setLastname] = React.useState('')
  const [companyName, setCompanyName] = React.useState('')
  const [errors, setErrors] = React.useState<ErrorsState>(null)
  const [creating, setCreating] = React.useState(false)
  const [result, setResult] = React.useState<CreateEnterpriseUserResult | null>(null)
  const [copied, setCopied] = React.useState(false)

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const requestData = { firstname, lastname, companyName }
    const error = validateForm(schema, requestData)

    if (error) setErrors(error)
    else {
      setErrors(null)
      setCreating(true)
      dispatch(
        createEnterpriseUser(
          {
            firstname: firstname || undefined,
            lastname: lastname || undefined,
            companyName: companyName || undefined,
          },
          (data) => {
            setCreating(false)
            setResult(data)
          },
          () => setCreating(false),
        ),
      )
    }
  }

  const handleCopy = () => {
    if (!result) return
    navigator.clipboard.writeText(result.accessKey)
    setCopied(true)
  }

  const handleDone = () => {
    onCreated()
    onClose()
  }

  if (result) {
    return (
      <Dialog open={true} fullWidth onClose={handleDone}>
        <DialogTitle>{t('enterprise.create.createdTitle')}</DialogTitle>

        <Divider variant="popup" color="primary" />

        <Stack spacing={2} sx={{ p: 2 }}>
          <Typography variant="body2" color="textSecondary">
            {t('enterprise.create.createdHint')}
          </Typography>

          <FormControl fullWidth>
            <OutlinedInput
              value={result.accessKey}
              readOnly
              sx={{ fontFamily: 'monospace' }}
              endAdornment={
                <InputAdornment position="end">
                  <Tooltip title={<span>{copied ? t('common.copied') : t('common.copy')}</span>}>
                    <IconButton onClick={handleCopy} size="small">
                      <CopyIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              }
            />
          </FormControl>
        </Stack>

        <Divider variant="popup" color="primary" />

        <DialogActions>
          <Button onClick={handleDone} variant="contained" size="large" color="primary">
            {t('common.done')}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Dialog open={true} fullWidth onClose={creating ? undefined : onClose}>
      <DialogTitle>{t('enterprise.create.title')}</DialogTitle>

      <Divider variant="popup" color="primary" />

      <form onSubmit={handleSubmit} noValidate>
        <Stack spacing={2} sx={{ p: 2 }}>
          <Grid container>
            <Grid item xs={5}>
              <OptionalLabel label={t('field.label.firstname')} />
            </Grid>
            <Grid item xs={7}>
              <FormControl fullWidth>
                <OutlinedInput
                  value={firstname}
                  placeholder={t('field.hint.firstname')}
                  autoFocus
                  disabled={creating}
                  inputProps={{ maxLength: 50 }}
                  onChange={(e) => preventCyrillicOnInput(e, setFirstname)}
                />
                <FormError error={errors?.firstname} />
              </FormControl>
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={5}>
              <OptionalLabel label={t('field.label.lastname')} />
            </Grid>
            <Grid item xs={7}>
              <FormControl fullWidth>
                <OutlinedInput
                  value={lastname}
                  placeholder={t('field.hint.lastname')}
                  disabled={creating}
                  inputProps={{ maxLength: 50 }}
                  onChange={(e) => preventCyrillicOnInput(e, setLastname)}
                />
                <FormError error={errors?.lastname} />
              </FormControl>
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={5}>
              <OptionalLabel label={t('field.label.companyName')} />
            </Grid>
            <Grid item xs={7}>
              <FormControl fullWidth>
                <OutlinedInput
                  value={companyName}
                  disabled={creating}
                  inputProps={{ maxLength: 120 }}
                  onChange={(e) => setCompanyName(e.target.value)}
                />
                <FormError error={errors?.companyName} />
              </FormControl>
            </Grid>
          </Grid>
        </Stack>

        <Divider variant="popup" color="primary" />

        <DialogActions>
          <Button onClick={onClose} color="primary" size="large" disabled={creating}>
            {t('common.close')}
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            size="large"
            color="primary"
            loading={creating}
          >
            {t('common.save')}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  )
}
